import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Loader2, Users, Shield } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getShareLink, joinBoardViaShareLink } from '../firebase/firestore';
import { BoardShareLink } from '../types';

const JoinBoard: React.FC = () => {
  const { shareLinkId } = useParams<{ shareLinkId: string }>();
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [shareLink, setShareLink] = useState<BoardShareLink | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isJoining, setIsJoining] = useState(false);
  const [joinStatus, setJoinStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    const loadShareLink = async () => {
      if (!shareLinkId) {
        setError('Invalid share link');
        setIsLoading(false);
        return;
      }

      try {
        const link = await getShareLink(shareLinkId);
        if (!link) {
          setError('Share link not found');
          setIsLoading(false);
          return;
        }

        if (!link.isActive) {
          setError('This share link has been deactivated');
          setIsLoading(false);
          return;
        }

        if (link.expiresAt && new Date() > link.expiresAt) {
          setError('This share link has expired');
          setIsLoading(false);
          return;
        }

        setShareLink(link);
      } catch (err) {
        console.error('Error loading share link:', err);
        setError('Failed to load share link');
      } finally {
        setIsLoading(false);
      }
    };

    loadShareLink();
  }, [shareLinkId]);

  useEffect(() => {
    // If user is authenticated and share link is valid, automatically join
    if (user && shareLink && !isJoining && joinStatus === 'idle') {
      handleJoinBoard();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleJoinBoard triggers state that would cause loop
  }, [user, shareLink, isJoining, joinStatus]);

  const handleJoinBoard = async () => {
    if (!user || !shareLink) return;

    setIsJoining(true);
    setJoinStatus('idle');

    try {
      await joinBoardViaShareLink(shareLinkId!);

      setJoinStatus('success');
      
      // Redirect to the board after a short delay
      setTimeout(() => {
        navigate(`/board/${shareLink.boardId}`);
      }, 2000);
    } catch (error) {
      console.error('Error joining board:', error);
      setJoinStatus('error');
      setError('Failed to join board. You may already be a member.');
    } finally {
      setIsJoining(false);
    }
  };

  const handleSignIn = () => {
    // This will trigger the auth flow, and once signed in, the useEffect will handle joining
  };

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-heian-50 via-washi-50 to-sakura-50 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <Loader2 className="w-8 h-8 animate-spin text-satsuma-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-heian-50 via-washi-50 to-sakura-50 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md mx-auto p-6"
        >
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Link Error</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="btn-primary"
          >
            Go to Home
          </button>
        </motion.div>
      </div>
    );
  }

  if (!shareLink) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-heian-50 via-washi-50 to-sakura-50 flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="glass-effect rounded-3xl p-8 max-w-md mx-auto text-center"
      >
        {!user ? (
          <>
            <Users className="w-16 h-16 text-satsuma-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Join Board</h1>
            <p className="text-gray-600 mb-4">
              You've been invited to join <strong>{shareLink.boardName}</strong>
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-center space-x-2 mb-2">
                <Shield className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-800">
                  Role: {shareLink.role}
                </span>
              </div>
              <p className="text-xs text-blue-700">
                You'll be added to the board with {shareLink.role} permissions
              </p>
            </div>
            <p className="text-gray-500 text-sm mb-6">
              Sign in to automatically join the board
            </p>
            <button
              onClick={handleSignIn}
              className="btn-primary w-full"
            >
              Sign In to Join
            </button>
          </>
        ) : joinStatus === 'success' ? (
          <>
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Welcome!</h1>
            <p className="text-gray-600 mb-4">
              You've successfully joined <strong>{shareLink.boardName}</strong>
            </p>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-green-700">
                Redirecting you to the board...
              </p>
            </div>
          </>
        ) : joinStatus === 'error' ? (
          <>
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Join Failed</h1>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="btn-primary"
            >
              Go to Home
            </button>
          </>
        ) : (
          <>
            <Loader2 className="w-8 h-8 animate-spin text-satsuma-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Joining Board</h1>
            <p className="text-gray-600">
              Adding you to <strong>{shareLink.boardName}</strong>...
            </p>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default JoinBoard;
